import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import { 
  LibraryFilters,
  LibraryVillageGroup,
  LibraryAsset,
  LibraryPaginationInfo,
  LibraryListResponse,
  LibraryAssetResponse,
  LibraryUpdateResponse,
  LibraryAssetUpdate,
  UseLibraryAssetsReturn
} from '../types/library';

const LIBRARY_LIST_ENDPOINT = '/console/asset_management/library/list';
const LIBRARY_ASSET_ENDPOINT = '/console/asset_management/library';

export const useLibraryAssets = (initialFilters: LibraryFilters): UseLibraryAssetsReturn => {
  const [villages, setVillages] = useState<LibraryVillageGroup[]>([]);
  const [assets, setAssets] = useState<LibraryAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<LibraryPaginationInfo>({
    page_number: 1,
    page_size: 10,
    group_number: 1,
    group_size: 10,
    has_next_page: false,
    village_has_next_page: false
  });

  const [currentFilters, setCurrentFilters] = useState<LibraryFilters>(initialFilters);
  const [hasMoreVillages, setHasMoreVillages] = useState(false);

  // Build query string from filters
  const buildQueryString = useCallback((filters: LibraryFilters): string => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    return params.toString();
  }, []);

  // Fetch assets from API
  const fetchAssets = useCallback(async (filters: LibraryFilters, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const queryString = buildQueryString(filters);
      const url = `${LIBRARY_LIST_ENDPOINT}?${queryString}`;
      
      console.log('Fetching library assets from:', url);
      
      const response = await apiClient.get<LibraryListResponse>(url);
      
      if (response.data) {
        const { data } = response.data;
        
        // Get all villages from API
        let allVillages = data.list || [];
        
        // Implement client-side pagination limits for safety
        const villagePageSize = filters.page_size || 10;
        const assetGroupSize = filters.group_size || 10;
        
        let processedVillages: LibraryVillageGroup[];
        
        if (!append) {
          // For initial load, take villages as-is from API response
          processedVillages = allVillages;
        } else {
          // For load more, calculate which villages to show next
          const currentVillageCount = villages.length;
          const nextBatch = allVillages.slice(currentVillageCount, currentVillageCount + villagePageSize);
          processedVillages = nextBatch;
        }
        
        // Update villages - append if load more, replace if initial load
        setVillages(prev => {
          if (append) {
            // Append new villages, ensuring no duplicates
            const existingIds = new Set(prev.map(v => v.village_id));
            const newVillages = processedVillages.filter(v => !existingIds.has(v.village_id));
            return [...prev, ...newVillages];
          } else {
            return processedVillages;
          }
        });
        
        // Check if there are more villages available
        const hasMoreVillagesAvailable = data.has_next_page;
        setHasMoreVillages(hasMoreVillagesAvailable);
        
        // Flatten all assets from processed villages
        const newAssets = processedVillages.reduce<LibraryAsset[]>((acc, village) => {
          return [...acc, ...village.assets];
        }, []);
        
        // Update assets - append if load more, replace if initial load
        setAssets(prev => {
          if (append) {
            // Append new assets, ensuring no duplicates
            const existingIds = new Set(prev.map(a => a.id));
            const uniqueNewAssets = newAssets.filter(a => !existingIds.has(a.id));
            return [...prev, ...uniqueNewAssets];
          } else {
            return newAssets;
          }
        });
        
        // Update pagination info
        setPagination({
          page_number: data.page_number,
          page_size: data.page_size,
          group_number: filters.group || 1,
          group_size: assetGroupSize,
          has_next_page: data.has_next_page,
          village_has_next_page: processedVillages.some(v => v.has_next_page || false)
        });

        console.log('Library assets loaded successfully:', {
          totalVillagesFromApi: allVillages.length,
          processedVillages: processedVillages.length,
          totalAssets: newAssets.length,
          hasMoreVillages: hasMoreVillagesAvailable,
          pagination: data,
          append,
          villagePageSize,
          assetGroupSize
        });
      }
    } catch (err) {
      console.error('Error fetching library assets:', err);
      
      let errorMessage = 'Failed to load library assets';
      
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.status) {
          errorMessage = `HTTP Error ${response.status}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      if (!append) {
        setVillages([]);
        setAssets([]);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [buildQueryString, villages.length]);

  // Refetch with current filters
  const refetch = useCallback(() => {
    fetchAssets(currentFilters);
  }, [fetchAssets, currentFilters]);

  // Update filters and refetch
  const updateFilters = useCallback((newFilters: LibraryFilters) => {
    console.log('Updating library filters:', newFilters);
    setCurrentFilters(newFilters);
    fetchAssets(newFilters);
  }, [fetchAssets]);

  // Load more villages (next page)
  const loadMoreVillages = useCallback(() => {
    if (!hasMoreVillages || loadingMore) return;
    
    const nextPageFilters = {
      ...currentFilters,
      page: (pagination.page_number || 1) + 1
    };
    
    fetchAssets(nextPageFilters, true);
  }, [currentFilters, pagination.page_number, hasMoreVillages, loadingMore, fetchAssets]);

  // Load more assets within a specific village
  const loadMoreAssetsForVillage = useCallback(async (villageId: string) => {
    const village = villages.find(v => v.village_id === villageId);
    if (!village || !village.has_next_page) return;

    try {
      setLoadingMore(true);
      
      // Use user's filter settings with village-specific parameters
      const userGroupSize = currentFilters.group_size || 10;
      const currentAssetCount = village.assets.length;
      const nextGroup = Math.ceil(currentAssetCount / userGroupSize) + 1;
      
      console.log('Loading more assets for village:', {
        villageId,
        userGroupSize,
        currentAssetCount,
        nextGroup,
        villageName: village.village_name
      });
      
      const villageFilters = {
        ...currentFilters,
        village_id: villageId,
        page: 1,
        page_size: 1, // Only get the specific village
        group: nextGroup,
        group_size: userGroupSize
      };

      const queryString = buildQueryString(villageFilters);
      const url = `${LIBRARY_LIST_ENDPOINT}?${queryString}`;
      
      console.log('Fetching more assets for village:', {
        villageId,
        url,
        filters: villageFilters
      });
      
      const response = await apiClient.get<LibraryListResponse>(url);
      
      console.log('API Response for load more assets:', {
        status: response.status,
        hasData: !!response.data,
        villageCount: response.data?.data?.list?.length || 0
      });
      
      if (response.data?.data?.list?.length > 0) {
        const updatedVillageData = response.data.data.list.find(v => v.village_id === villageId);
        
        console.log('Found village in response:', {
          found: !!updatedVillageData,
          assetCount: updatedVillageData?.assets?.length || 0
        });
        
        if (updatedVillageData && updatedVillageData.assets.length > 0) {
          const newAssets = updatedVillageData.assets;
          const hasMoreAssets = updatedVillageData.has_next_page || false;
          
          setVillages(prev => {
            return prev.map(v => {
              if (v.village_id === villageId) {
                return {
                  ...v,
                  assets: [...v.assets, ...newAssets],
                  has_next_page: hasMoreAssets,
                  group_number: nextGroup
                };
              }
              return v;
            });
          });

          // Update flattened assets array
          setAssets(prev => {
            const existingIds = new Set(prev.map(a => a.id));
            const uniqueNewAssets = newAssets.filter(a => !existingIds.has(a.id));
            return [...prev, ...uniqueNewAssets];
          });
        }
      } else {
        console.log('No more assets available for village');
      }
    } catch (err) {
      console.error('Error loading more assets for village:', {
        villageId,
        error: err,
        message: err instanceof Error ? err.message : 'Unknown error'
      });
      
      let errorMessage = 'Failed to load more assets for this village';
      
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.status) {
          errorMessage = `HTTP Error ${response.status}: Failed to load more assets`;
        }
      } else if (err instanceof Error) {
        errorMessage = `Load More Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoadingMore(false);
    }
  }, [villages, currentFilters, buildQueryString]);

  // Get single asset by ID
  const getAssetById = useCallback(async (id: number): Promise<LibraryAsset | null> => {
    try {
      const url = `${LIBRARY_ASSET_ENDPOINT}/${id}`;
      const response = await apiClient.get<LibraryAssetResponse>(url);
      
      if (response.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching asset by ID:', err);
      return null;
    }
  }, []);

  // Update asset metadata
  const updateAsset = useCallback(async (id: number, updates: LibraryAssetUpdate): Promise<boolean> => {
    try {
      const url = `${LIBRARY_ASSET_ENDPOINT}/${id}`;
      const response = await apiClient.put<LibraryUpdateResponse>(url, updates);
      
      if (response.data?.data?.id) {
        // Refresh the assets to get updated data
        refetch();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating asset:', err);
      return false;
    }
  }, [refetch]);

  // Initial load
  useEffect(() => {
    fetchAssets(currentFilters);
  }, []); // Only run once on mount

  return {
    assets,
    villages,
    loading,
    loadingMore,
    error,
    pagination,
    hasMoreVillages,
    refetch,
    updateFilters,
    loadMoreVillages,
    loadMoreAssetsForVillage,
    getAssetById,
    updateAsset
  };
};